import { useEffect, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useDevMode } from "@/hooks/useDevMode";
import { useLeadSupabaseSync } from "@/hooks/useLeadSupabaseSync";
import { useLeadWorkspaceBootstrap } from "./useLeadWorkspaceBootstrap";
import { usePersistedLeadWorkspace } from "@/hooks/usePersistedLeadWorkspace";
import type { Salesperson, Lead } from "../../types/lead";
import type { Location } from "../../types/location";
import { useVoiceSettings } from "@/hooks/useVoiceSettings";
import { useLeadCoaching } from "@/hooks/useLeadCoaching";
import { textHeuristics } from "@/lib/lead/extraction/textHeuristics";
import { canViewOrgWideSalesRecords } from "@/lib/sales/orgRecordVisibility";
import type { PrimaryMode } from "./sections/LeadWorkspaceHeaderAccess";

type Params = {
  DEV_MODE_KEY: string;
  setTrainingMode: Dispatch<SetStateAction<boolean>>;
  trainingMode: boolean;
  isListening: boolean;
  mode: PrimaryMode;
  setMode: Dispatch<SetStateAction<PrimaryMode>>;
  currentLead: Lead | null;
  location: Location | null;
  salesperson: Salesperson | null;
  locations: any[];
  locationsLoaded: boolean;
  setSalesperson: Dispatch<SetStateAction<Salesperson | null>>;
  setLocation: Dispatch<SetStateAction<Location | null>>;
  setLocations: Dispatch<SetStateAction<any[]>>;
  setLocationsLoaded: Dispatch<SetStateAction<boolean>>;
  leads: Lead[];
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  speakFn: (text: string) => void;
};

export function useLeadWorkspaceServices(params: Params) {
  const auth = useAuthOptional();
  const scopedOrganizationId = useScopedOrganizationId();
  const leadWorkspaceStorageKey = useMemo(
    () => (scopedOrganizationId ? `bennett-buddy-org-${scopedOrganizationId}` : "bennett-buddy"),
    [scopedOrganizationId]
  );

  const {
    devMode,
    devLoginOpen,
    setDevLoginOpen,
    devLoginPassword,
    setDevLoginPassword,
    devLoginError,
    devLoginBusy,
    requestDevMode,
    disableDevMode,
    submitDevLogin,
    getSessionInfo,
  } = useDevMode({
    devModeKey: params.DEV_MODE_KEY,
    setTrainingMode: params.setTrainingMode,
  });

  const viewOrgWideLeads =
    auth?.status === "ready" && canViewOrgWideSalesRecords((...r) => auth.hasRole(...r));

  const { archiveLead, purgeLeads, syncAllLeads, syncedCount, viableCount, isSyncing, isInitialSyncComplete } =
    useLeadSupabaseSync({
      leads: params.leads,
      setLeads: params.setLeads,
      userId: auth?.session?.user?.id ?? null,
      organizationId: scopedOrganizationId ?? null,
      locationId: params.location?.id ?? null,
      locationName: params.location?.name ?? null,
      viewOrgWide: viewOrgWideLeads,
    });

  const { selectableLocations, ghlWebhookUrl, refreshLocations } = useLeadWorkspaceBootstrap({
    auth,
    mode: params.mode,
    setMode: params.setMode,
    trainingMode: params.trainingMode,
    setTrainingMode: params.setTrainingMode,
    isListening: params.isListening,
    currentLead: params.currentLead,
    location: params.location,
    salesperson: params.salesperson,
    scopedOrganizationId,
    locations: params.locations,
    locationsLoaded: params.locationsLoaded,
    setSalesperson: params.setSalesperson,
    setLocation: params.setLocation,
    setLocations: params.setLocations,
    setLocationsLoaded: params.setLocationsLoaded,
  });

  usePersistedLeadWorkspace<Salesperson, Location>({
    storageKey: leadWorkspaceStorageKey,
    setSalesperson: params.setSalesperson,
    setLocation: params.setLocation,
    setLeads: params.setLeads,
    salesperson: params.salesperson,
    location: params.location,
    leads: params.leads,
  });

  const { voiceEnabled, voiceProvider, speakMode, speakAnswers, canSpeakHelp, setVoiceEnabled, setVoiceProvider, setSpeakMode } =
    useVoiceSettings();

  const { coachIfNeeded, resetCoachState } = useLeadCoaching({
    canSpeakHelp,
    speak: canSpeakHelp ? params.speakFn : undefined,
    normalizePhone: textHeuristics.normalizePhone,
  });

  useEffect(() => {
    disableDevMode();
  }, [disableDevMode]);

  return {
    auth,
    scopedOrganizationId,
    leadWorkspaceStorageKey,
    viewOrgWideLeads,
    devMode,
    devLoginOpen,
    setDevLoginOpen,
    devLoginPassword,
    setDevLoginPassword,
    devLoginError,
    devLoginBusy,
    requestDevMode,
    disableDevMode,
    submitDevLogin,
    getSessionInfo,
    archiveLead,
    purgeLeads,
    syncAllLeads,
    syncedCount,
    viableCount,
    isSyncing,
    isInitialSyncComplete,
    selectableLocations,
    ghlWebhookUrl,
    refreshLocations,
    voiceEnabled,
    voiceProvider,
    speakMode,
    speakAnswers,
    canSpeakHelp,
    setVoiceEnabled,
    setVoiceProvider,
    setSpeakMode,
    coachIfNeeded,
    resetCoachState,
  };
}
